// What a zone looks like: its painted ground, the props that stand on it (y-sorted with people), flat
// decals (boats, lily pads), where light comes from at night, and a little ambient life.
//
// The town square keeps the original hand-placed art. Every other zone is painted from the art list its
// ZoneBuilder made (shared/zones.ts), in the same style: organic edges, noise shading, dithering.
import {
  C, INK, ground as TOWN_GROUND, PROPS as TOWN_PROPS, DECALS as TOWN_DECALS, drawAmbient as townAmbient,
  OAK, BLOSSOM, PINE, REEDS, MUSHROOM, BARREL, CRATE, BOAT, BENCH, LAMP, STALL, FENCE_H, FENCE_V, CABBAGE, CARROT, SIGN, ROCK, POT, BUSH, TUFT, LILY,
  house, hash, vnoise, dith, ramp, sprite, inEll,
} from '../../legacy/art.gen.js';
import { T, type PropDef, type ZoneArt } from '../../../shared/zonebuild.js';
import { zoneDef } from '../../../shared/zones.js';
import type { Light } from './sky.js';

const TS = 16;
export interface Prop { img: HTMLCanvasElement; x: number; y: number; ox: number; oy: number; shadow?: number; house?: { x: number; y: number; w: number; h: number }; kind?: string }
export interface Decal { img: HTMLCanvasElement; x: number; y: number }
export interface Scene {
  id: string; w: number; h: number;
  ground: HTMLCanvasElement;
  decals: Decal[];
  props: Prop[];
  lamps: Light[];
  /** Ground with decals baked in (the sightseeing camera's floor). */
  flat(): HTMLCanvasElement;
  ambient(ctx: CanvasRenderingContext2D, t: number, cx: number, cy: number, vw: number, vh: number): void;
  /** Small colour picture of the zone, one pixel per tile (minimap and world map). */
  thumb(): HTMLCanvasElement;
  /** Terrain per tile (T values), for the maps. */
  tiles: Uint8Array;
}

// ---------- sprites the old town didn't have ----------
const WAYSHRINE = sprite(16, 34, (x: number, y: number) => {
  // a little stone shrine with a glowing crystal on top
  if (inEll(x, y, 7.5, 6, 4.2, 5.6)) return inEll(x, y, 6.5, 4.5, 1.6, 2) ? '#f2fbff' : x < 7 ? '#a3def4' : x > 9 ? '#5b8fc7' : '#7cc0d8';
  if (y >= 11 && y <= 13 && x >= 3 && x <= 12) return y === 11 ? '#f3eee3' : x === 3 || x === 12 ? INK : '#cfc3a8';
  if (y >= 14 && y <= 29 && x >= 5 && x <= 10) return x === 5 || x === 10 ? INK : x === 6 ? '#efe8d8' : (y % 5 === 0 ? '#bfae8c' : '#ddd4c0');
  if (y >= 30 && y <= 33 && x >= 2 && x <= 13) return y === 30 ? '#f3eee3' : x === 2 || x === 13 || y === 33 ? INK : '#cfc3a8';
  return null;
}) as HTMLCanvasElement;
const WELL = sprite(28, 30, (x: number, y: number) => {
  if (y <= 3 && x >= 2 && x <= 25) return y === 0 ? INK : (x + y) % 5 === 0 ? '#8c3a30' : '#b4473b';       // little roof
  if (y > 3 && y <= 14 && (x === 4 || x === 5 || x === 22 || x === 23)) return x === 4 || x === 22 ? '#9a6a3e' : '#c78659';
  if (y === 9 && x > 5 && x < 22) return '#7a473f';                                                         // the crank
  if (y >= 15 && y <= 29 && x >= 1 && x <= 26) {
    if (y === 15 || y === 29 || x === 1 || x === 26) return INK;
    if (y <= 18) return y === 16 ? '#3d6a8f' : '#2f4f6b';
    const row = Math.floor((y - 19) / 3), lx = (x + (row % 2) * 3) % 6;
    return lx === 0 || (y - 19) % 3 === 2 ? '#bfae8c' : '#e3dccb';
  }
  return null;
}) as HTMLCanvasElement;
const ANVIL = sprite(16, 11, (x: number, y: number) => {
  if (y <= 3 && x >= 1 && x <= 14) return y === 0 ? '#8a8f99' : x === 1 || x === 14 || y === 3 ? INK : '#5a5e6b';
  if (y >= 4 && y <= 6 && x >= 5 && x <= 10) return x === 5 || x === 10 ? INK : '#4a4e58';
  if (y >= 7 && x >= 3 && x <= 12) return y === 10 || x === 3 || x === 12 ? INK : '#5a5e6b';
  return null;
}) as HTMLCanvasElement;
/** The town gate: two posts and a beam across the road, with the way out lettered on it. */
function gateArch(widthTiles: number): HTMLCanvasElement {
  const W = widthTiles * TS - 8, H = 46;
  const cv = sprite(W, H, (x: number, y: number) => {
    const post = x < 8 || x >= W - 8;
    if (post && y >= 8) return x === 0 || x === 7 || x === W - 8 || x === W - 1 ? INK : (x % 8 === 2 ? '#d49364' : y % 9 === 0 ? '#9a6a3e' : '#c78659');
    if (y >= 2 && y <= 13) return y === 2 || y === 13 || x === 0 || x === W - 1 ? INK : y === 3 ? '#e0a87a' : y === 12 ? '#9a6a3e' : '#c78659';
    if (y < 2 && (x < 10 || x > W - 11)) return INK;
    return null;
  }) as HTMLCanvasElement;
  const g = cv.getContext('2d')!;
  g.fillStyle = '#fbf7ee'; g.font = '7px "Pixelify Sans", monospace'; g.textAlign = 'center'; g.fillText('MEADOWLANDS', W / 2, 11);
  return cv;
}

const TREES: Record<string, HTMLCanvasElement> = { oak: OAK, blossom: BLOSSOM, pine: PINE };

/** Turn one art entry into what gets drawn. */
function toProps(p: PropDef, decals: Decal[], lamps: Light[]): Prop[] {
  const bx = p.x * TS, by = p.y * TS, v = p.v ?? 0;
  switch (p.kind) {
    case 'oak': case 'blossom': case 'pine': { const pine = p.kind === 'pine'; return [{ img: TREES[p.kind], x: bx + 8, y: by + TS - 1, ox: pine ? 13 : 17, oy: pine ? 43 : 40, shadow: 12 }]; }
    case 'bush': return [{ img: BUSH[v % 3], x: bx + 8, y: by + 14, ox: 11, oy: 15 }];
    case 'tuft': return [{ img: TUFT[v % 2], x: bx + 5 + (hash(p.x, p.y) % 6), y: by + 12, ox: 8, oy: 14 }];
    case 'mushroom': return [{ img: MUSHROOM, x: bx + 6 + (hash(p.x, p.y, 3) % 5), y: by + 12, ox: 5, oy: 9 }];
    case 'rock': return [{ img: ROCK, x: bx + 8, y: by + 12, ox: 7, oy: 9 }];
    case 'reeds': return [{ img: REEDS, x: bx + 8, y: by + TS - 4, ox: 7, oy: 21 }];
    case 'lamp': lamps.push({ x: bx + 8, y: by + TS - 2 - 29, r: 54, warm: true }); return [{ img: LAMP, x: bx + 8, y: by + TS - 2, ox: 6, oy: 34, kind: 'lamp' }];
    case 'bench': return [{ img: BENCH, x: bx + 16, y: by + TS - 2, ox: 16, oy: 16 }];
    case 'barrel': return [{ img: BARREL, x: bx + 8, y: by + TS - 1, ox: BARREL.width / 2, oy: BARREL.height - 1 }];
    case 'crate': return [{ img: CRATE, x: bx + 8, y: by + TS - 1, ox: CRATE.width / 2, oy: CRATE.height - 1 }];
    case 'pot': return [{ img: POT[v % 3], x: bx + 8, y: by + TS - 2, ox: 6, oy: 15 }];
    case 'fence_h': return [{ img: FENCE_H, x: bx + 8, y: by + 14, ox: 8, oy: 14 }];
    case 'fence_v': return [{ img: FENCE_V, x: bx + 8, y: by + 16, ox: 4, oy: 18 }];
    case 'sign': return [{ img: SIGN, x: bx + 8, y: by + TS - 2, ox: 9, oy: 20 }];
    case 'stall': return [{ img: STALL, x: bx + 2, y: by + 14, ox: 0, oy: 44 }];
    case 'cabbage': return [{ img: CABBAGE, x: bx + 8, y: by + 12, ox: 6, oy: 9 }];
    case 'carrot': return [{ img: CARROT, x: bx + 8, y: by + 12, ox: 6, oy: 9 }];
    case 'boat': decals.push({ img: BOAT, x: bx + 2, y: by + 6 }); return [];
    case 'lily': decals.push({ img: LILY[v % 2], x: bx + 2, y: by + 4 }); return [];
    case 'wayshrine': lamps.push({ x: bx + 8, y: by + 6, r: 34 }); return [{ img: WAYSHRINE, x: bx + 8, y: by + TS - 2, ox: 8, oy: 33, kind: 'wayshrine' }];
    case 'well': return [{ img: WELL, x: bx + 8, y: by + TS - 1, ox: 14, oy: 29 }];
    case 'anvil': return [{ img: ANVIL, x: bx + 8, y: by + TS - 2, ox: 8, oy: 10 }];
    case 'arch': { const img = gateArch(6); return [{ img, x: bx - 2 * TS + 4, y: by + TS + 2, ox: 0, oy: img.height - 1 }]; }
    case 'house': {
      const h = p.house!, b = { name: h.name, x: p.x, y: p.y, w: h.w, h: h.h, roof: h.roof, roofD: h.roofD, door: h.door, icon: h.icon };
      lamps.push({ x: (b.x + 1.4) * TS, y: (b.y + b.h) * TS - 22, r: 30, warm: true }, { x: (b.x + b.w - 1.4) * TS, y: (b.y + b.h) * TS - 22, r: 30, warm: true });
      return [{ img: house(b), x: b.x * TS, y: (b.y + b.h) * TS, ox: 0, oy: b.h * TS + 6, house: { x: b.x, y: b.y, w: b.w, h: b.h } }];
    }
  }
  return [];
}

// ---------- the ground painter ----------
const hex = (c: string) => parseInt(c.slice(1), 16);
const GRS = ['#8fbf5a', '#9fca6d', '#aed287', '#bcdb92', '#c9e2a3'], DIRT = ['#d6b68e', '#e0c49f', '#e9d2b1', '#f2e2c6'], WAT = ['#7cc0d8', '#8fcfe8', '#a3def4'];
const STONES = ['#e3dccb', '#ddd4c0', '#e9e3d5', '#d6ccb6'], SAND = ['#e6d3a6', '#ecdcb4', '#f1e4c3', '#f5ebd0'];

/** For each pixel, how far (in pixels, Chebyshev, capped at 3) the nearest pixel of `type` is. */
function nearness(ter: Uint8Array, W: number, H: number, type: number): Uint8Array {
  const hz = new Uint8Array(W * H), out = new Uint8Array(W * H);
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    let d = 3; for (let k = -2; k <= 2 && d > Math.abs(k); k++) { const nx = x + k; if (nx >= 0 && nx < W && ter[y * W + nx] === type) d = Math.abs(k); }
    hz[y * W + x] = d;
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    let d = 3; for (let k = -2; k <= 2; k++) { const ny = y + k; if (ny >= 0 && ny < H) d = Math.min(d, Math.max(Math.abs(k), hz[ny * W + x])); }
    out[y * W + x] = d;
  }
  return out;
}

function paint(art: ZoneArt, collision: string[]): { ground: HTMLCanvasElement; ter: Uint8Array } {
  const MW = art.w, MH = art.h, W = MW * TS, H = MH * TS, G = (tx: number, ty: number) => +art.ground[Math.min(MH - 1, Math.max(0, ty)) * MW + Math.min(MW - 1, Math.max(0, tx))];
  const rawAt = (px: number, py: number) => G(Math.floor(px / TS), Math.floor(py / TS));
  const ter = new Uint8Array(W * H);
  for (let py = 0; py < H; py++) for (let px = 0; px < W; px++) {
    const raw = rawAt(px, py); let t = raw;
    if (raw !== T.STONE && raw !== T.PIER) { const j = (vnoise(px, py, 9, 1) - 0.5) * 7, k = (vnoise(px, py, 9, 2) - 0.5) * 7, w2 = rawAt(px + j, py + k); t = w2 === T.STONE || w2 === T.PIER ? raw : w2; }
    ter[py * W + px] = t;
  }
  const dist = new Int16Array(W * H).fill(999), q: number[] = [];
  for (let i = 0; i < ter.length; i++) if (ter[i] !== T.WATER) { dist[i] = 0; q.push(i); }
  for (let h = 0; h < q.length; h++) {
    const i = q[h], x = i % W, y = (i / W) | 0;
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) { const nx = x + dx, ny = y + dy; if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue; const j = ny * W + nx; if (dist[j] > dist[i] + 1) { dist[j] = dist[i] + 1; q.push(j); } }
  }
  const nW = nearness(ter, W, H, T.WATER), nP = nearness(ter, W, H, T.PATH), nG = nearness(ter, W, H, T.GRASS), nPier = nearness(ter, W, H, T.PIER), nSand = nearness(ter, W, H, T.SAND);
  const cv = document.createElement('canvas'); cv.width = W; cv.height = H;
  const g = cv.getContext('2d')!, img = g.createImageData(W, H), D = img.data;
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    const i = y * W + x, t = ter[i]; let c: string;
    if (t === T.GRASS) {
      if (nW[i] <= 2) c = nW[i] <= 1 ? '#d6c49a' : '#e2d3ae';
      else if (nP[i] <= 1 || nSand[i] <= 1) c = GRS[0];
      else { const v = vnoise(x, y, 26, 3) * 0.7 + vnoise(x, y, 7, 4) * 0.3 + (dith(x, y) - 0.5) * 0.22; c = ramp((v - 0.18) / 0.66, GRS.slice(1)); }
    } else if (t === T.PATH) {
      if (nG[i] <= 1) c = '#c9a67c';
      else { const v = vnoise(x, y, 11, 5) * 0.7 + vnoise(x, y, 3, 6) * 0.3 + (dith(x, y) - 0.5) * 0.2; c = ramp((v - 0.15) / 0.7, DIRT); }
    } else if (t === T.SAND) {
      if (nW[i] <= 1) c = '#d9c391';
      else { const v = vnoise(x, y, 13, 8) * 0.7 + vnoise(x, y, 4, 9) * 0.3 + (dith(x, y) - 0.5) * 0.25; c = ramp((v - 0.15) / 0.7, SAND); if (hash(x, y, 11) % 97 === 0) c = '#fbf7ee'; }
    } else if (t === T.STONE) {
      if (nG[i] <= 1 || nP[i] <= 1 || nW[i] <= 1 || nSand[i] <= 1) c = '#bfae8c';
      else {
        const row = Math.floor(y / 7), ly = y % 7, off = row % 2 ? 4 : 0, col = Math.floor((x + off) / 9), lx = (x + off) % 9;
        c = lx === 0 || ly === 0 ? '#c8b998' : lx === 1 || ly === 1 ? '#f3eee3' : lx === 8 || ly === 6 ? '#cfc3a8' : STONES[hash(col, row, 3) % 4];
      }
    } else if (t === T.WATER) {
      const d = dist[i] + (dith(x, y) - 0.5) * 3;
      c = dist[i] <= 1 ? '#f2fbff' : dist[i] === 2 ? '#cdeef9' : d < 7 ? WAT[2] : d < 13 ? WAT[1] : WAT[0];
      if (nPier[i] <= 2 && ((y >= 2 && ter[(y - 2) * W + x] === T.PIER) || (x >= 2 && ter[y * W + x - 2] === T.PIER))) c = '#6aaecb';
    } else if (t === T.SOIL) {
      const r = y % 5; c = r === 0 ? '#7d5532' : r === 1 ? '#b9875a' : (hash(x, y, 7) % 9 === 0 ? '#b9875a' : '#9a6a3e');
    } else {   // pier
      const row = Math.floor(y / 4), ly = y % 4, tx = Math.floor(x / TS), lx = x % TS;
      if (rawAt(x - 1, y) !== T.PIER || rawAt(x + 1, y) !== T.PIER) c = INK;
      else if (ly === 3) c = '#6e4a2c';
      else if ((lx === 2 || lx === 13) && ly === 1) c = '#5a3a2a';
      else if ((x * 7 + row * 13) % 11 === 0) c = '#a8683f';
      else c = ['#c78659', '#bf7d52', '#cf9064'][hash(row, tx, 2) % 3];
    }
    const n = hex(c), k = i * 4; D[k] = n >> 16; D[k + 1] = (n >> 8) & 255; D[k + 2] = n & 255; D[k + 3] = 255;
  }
  g.putImageData(img, 0, 0);
  const p = (x: number, y: number, col: string, w = 1, h = 1) => { g.fillStyle = col; g.fillRect(x, y, w, h); };
  const blocked = (tx: number, ty: number) => (collision[ty]?.[tx] ?? '#') !== '.' && collision[ty]?.[tx] !== '~';
  for (let ty = 1; ty < MH - 1; ty++) for (let tx = 1; tx < MW - 1; tx++) for (let k = 0; k < 4; k++) {
    const x = tx * TS + hash(tx, ty, k) % 15, y = ty * TS + hash(tx, ty, k + 9) % 15, t = ter[y * W + x];
    if (t === T.GRASS && nW[y * W + x] > 2) { p(x, y - 2, '#7cae55', 1, 2); p(x - 1, y - 1, '#7cae55'); p(x + 1, y - 1, '#7cae55'); p(x, y - 3, '#c9e2a3'); }
    if (t === T.PATH && k < 2) { p(x, y, '#b89c78', 3, 2); p(x, y, '#f3eee3'); p(x + 1, y, '#efe3cc'); }
    if (t === T.SAND && k === 0 && hash(tx, ty, 5) % 4 === 0) { p(x, y, '#f29bb8'); p(x + 1, y, '#fdc9ca'); }   // little shells
  }
  const flowers = Math.round(MW * MH / 12);
  for (let i = 0; i < flowers; i++) {
    const x = 24 + hash(i, 3, MW) % (W - 48), y = 24 + hash(i, 9, MH) % (H - 48);
    if (ter[y * W + x] !== T.GRASS || blocked(Math.floor(x / TS), Math.floor(y / TS)) || nW[y * W + x] <= 2) continue;
    const col = ['#f29bb8', '#fbf7ee', '#f9c776', '#a5bbe8', '#f5777d'][i % 5];
    for (let f = 0; f < 3; f++) { const fx = x + (f - 1) * 3, fy = y + (f % 2) * 2; p(fx, fy + 1, '#4e7d36', 1, 2); p(fx, fy - 1, col); p(fx - 1, fy, col); p(fx + 1, fy, col); p(fx, fy, '#f9c776'); }
  }
  return { ground: cv, ter };
}

// ---------- scenes ----------
const TILE_COLOR = ['#9fca6d', '#e0c49f', '#ddd4c0', '#8fcfe8', '#9a6a3e', '#c78659', '#ecdcb4'];
function makeThumb(w: number, h: number, tiles: Uint8Array, collision: string[], props: Prop[]): HTMLCanvasElement {
  const cv = document.createElement('canvas'); cv.width = w; cv.height = h;
  const g = cv.getContext('2d')!;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const t = tiles[y * w + x]; g.fillStyle = TILE_COLOR[t] ?? '#9fca6d';
    if (t === T.GRASS && /[#1-9]/.test(collision[y]?.[x] ?? '')) g.fillStyle = '#6f9a4a';      // trees and bushes: darker green
    g.fillRect(x, y, 1, 1);
  }
  for (const pr of props) if (pr.house) { const b = pr.house; g.fillStyle = '#b4473b'; g.fillRect(b.x, b.y, b.w, b.h - 1); g.fillStyle = '#efe4c8'; g.fillRect(b.x, b.y + b.h - 1, b.w, 1); }
  return cv;
}

function zoneAmbient(sc: { props: Prop[]; ter: Uint8Array; W: number; H: number }) {
  const chimneys = sc.props.filter(p => p.house).map(p => [p.x + p.house!.w * TS - 12, p.y - (p.house!.h * TS + 6)]);
  const shrines = sc.props.filter(p => p.kind === 'wayshrine');
  const flies = Array.from({ length: Math.max(3, Math.round(sc.W * sc.H / 90000)) }, (_, i) => ({ x: 40 + hash(i, 61) % (sc.W - 80), y: 40 + hash(i, 62) % (sc.H - 80), c: ['#f9c776', '#fbf7ee', '#a5bbe8', '#f29bb8'][i % 4], i }));
  const terAt = (x: number, y: number) => x < 0 || y < 0 || x >= sc.W || y >= sc.H ? T.GRASS : sc.ter[(y | 0) * sc.W + (x | 0)];
  return (ctx: CanvasRenderingContext2D, t: number, cx: number, cy: number, vw: number, vh: number) => {
    for (let i = 0; i < 40; i++) {   // glints on any water in view
      const x = cx + hash(i, 11) % vw + Math.sin(t * 0.6 + i) * 3, y = cy + hash(i, 12) % vh + ((t * 3 + i * 5) % 6);
      if (terAt(x, y) === T.WATER && terAt(x + 3, y) === T.WATER && terAt(x, y + 3) === T.WATER) { ctx.fillStyle = (i + Math.floor(t * 2)) % 3 ? '#e6f6fc' : '#ffffff'; ctx.fillRect(Math.round(x), Math.round(y), 3, 1); }
    }
    for (const [hx, hy] of chimneys) if (hx > cx - 20 && hx < cx + vw + 20 && hy > cy - 40 && hy < cy + vh + 20) for (let k = 0; k < 4; k++) {
      const ph = (t * 0.35 + k / 4) % 1, r = 1.5 + ph * 3.2;
      ctx.fillStyle = `rgba(251,247,238,${0.85 * (1 - ph)})`; ctx.beginPath(); ctx.arc(hx + Math.sin(ph * 5 + k) * 3 + ph * 6, hy - 2 - ph * 22, r, 0, Math.PI * 2); ctx.fill();
    }
    for (const s of shrines) {   // motes rising from the crystal
      for (let k = 0; k < 3; k++) { const ph = (t * 0.5 + k / 3) % 1; ctx.fillStyle = `rgba(242,251,255,${1 - ph})`; ctx.fillRect(Math.round(s.x - 3 + k * 3 + Math.sin(t * 2 + k) * 2), Math.round(s.y - 30 - ph * 16), 1, 1); }
    }
    for (const f of flies) {
      const x = f.x + Math.sin(t * 0.7 + f.i * 2) * 26, y = f.y + Math.sin(t * 1.3 + f.i) * 12 - 6, open = Math.floor(t * 8 + f.i) % 2;
      if (x < cx - 4 || x > cx + vw + 4 || y < cy - 4 || y > cy + vh + 4) continue;
      ctx.fillStyle = f.c; ctx.fillRect(Math.round(x) - (open ? 2 : 1), Math.round(y), open ? 2 : 1, 2); ctx.fillRect(Math.round(x) + 1, Math.round(y), open ? 2 : 1, 2);
      ctx.fillStyle = INK; ctx.fillRect(Math.round(x), Math.round(y), 1, 2);
    }
  };
}

const cache = new Map<string, Scene>();
function flatten(ground: HTMLCanvasElement, decals: Decal[]): () => HTMLCanvasElement {
  let out: HTMLCanvasElement | null = null;
  return () => { if (!out) { out = document.createElement('canvas'); out.width = ground.width; out.height = ground.height; const g = out.getContext('2d')!; g.drawImage(ground, 0, 0); for (const d of decals) g.drawImage(d.img, d.x, d.y); } return out; };
}

function townScene(collision: string[], w: number, h: number): Scene {
  const props: Prop[] = (TOWN_PROPS as Prop[]).slice(), decals = TOWN_DECALS as Decal[], lamps: Light[] = [];
  for (const pr of props) {
    if (pr.img.width === 12 && pr.img.height === 36) lamps.push({ x: pr.x, y: pr.y - 29, r: 54, warm: true });
    if (pr.house) { const b = pr.house; lamps.push({ x: (b.x + 1.4) * TS, y: (b.y + b.h) * TS - 22, r: 30, warm: true }, { x: (b.x + b.w - 1.4) * TS, y: (b.y + b.h) * TS - 22, r: 30, warm: true }); }
  }
  const ws = zoneDef('town')?.wayshrine;
  if (ws) props.push(...toProps({ kind: 'wayshrine', x: ws[0], y: ws[1] }, [], lamps));
  // the town's terrain, read back from its painted ground for the maps
  const tiles = new Uint8Array(w * h), g = TOWN_GROUND.getContext('2d')!;
  const px = g.getImageData(0, 0, w * TS, h * TS).data;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const k = ((y * TS + 8) * w * TS + x * TS + 8) * 4, r = px[k], gg = px[k + 1], b = px[k + 2];
    tiles[y * w + x] = b > 190 && b > r ? T.WATER : r > 200 && gg > 190 && b > 170 ? T.STONE : r > 190 && gg > 160 ? T.PATH : r < 170 && gg < 120 ? T.SOIL : T.GRASS;
  }
  const ambient = (ctx: CanvasRenderingContext2D, t: number, cx: number, cy: number, vw: number, vh: number) => {
    townAmbient(ctx, t, cx, cy, vw, vh);
    const s = props[props.length - 1];
    for (let k = 0; k < 3; k++) { const ph = (t * 0.5 + k / 3) % 1; ctx.fillStyle = `rgba(242,251,255,${1 - ph})`; ctx.fillRect(Math.round(s.x - 3 + k * 3 + Math.sin(t * 2 + k) * 2), Math.round(s.y - 30 - ph * 16), 1, 1); }
  };
  let thumb: HTMLCanvasElement | null = null;
  return { id: 'town', w, h, ground: TOWN_GROUND, decals, props, lamps, flat: flatten(TOWN_GROUND, decals), ambient, tiles,
    thumb: () => thumb ??= makeThumb(w, h, tiles, collision, props) };
}

/** The scene for an outdoor zone (null for rooms, which have their own drawing). Built once, on first visit. */
export function sceneFor(id: string, collision: string[], w: number, h: number): Scene | null {
  const hit = cache.get(id); if (hit) return hit;
  let sc: Scene | null = null;
  if (id === 'town') sc = townScene(collision, w, h);
  else {
    const def = zoneDef(id); if (!def?.art) return null;
    const art = def.art, decals: Decal[] = [], lamps: Light[] = [], props: Prop[] = [];
    for (const p of art.props) props.push(...toProps(p, decals, lamps));
    const { ground, ter } = paint(art, collision);
    const tiles = Uint8Array.from(art.ground, ch => +ch);
    let thumb: HTMLCanvasElement | null = null;
    sc = { id, w, h, ground, decals, props, lamps, flat: flatten(ground, decals), ambient: zoneAmbient({ props, ter, W: w * TS, H: h * TS }), tiles,
      thumb: () => thumb ??= makeThumb(w, h, tiles, collision, props) };
  }
  cache.set(id, sc);
  return sc;
}
export { C };
